import { Button, DialogFooter } from "frontend-components";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faCalendarAlt } from "@fortawesome/pro-solid-svg-icons";
import { faUser } from "@fortawesome/sharp-regular-svg-icons";
import { subject } from "@casl/ability";

import { type TaskWithOccurrence } from "@dashboard/utils/taskWithOccurrence";
import { TaskStatus, TaskStatusSelect } from "@dashboard/components/TaskStatusSelect";
import { OCCURRENCE_STATUS_CONFIG } from "@dashboard/utils/occurrenceStatusConfig";
import { Can } from "@dashboard/casl/taskAbility";

import { UserDisplay } from "../UserDisplay";

export function TaskViewBody({ task, onClose }: { task: TaskWithOccurrence; onClose: () => void }) {
    const { i18n, t } = useTranslation();
    const occurrenceStatus = task.occurrence?.status ?? "OPEN";
    const config = OCCURRENCE_STATUS_CONFIG[occurrenceStatus];

    return (
        <div className="flex flex-col gap-4 overflow-hidden">
            <div className="flex items-center gap-3">
                {task.occurrence ? (
                    <Can I="update" this={subject("TaskOccurrence", { task })} passThrough>
                        {(allowed) =>
                            allowed ? (
                                <TaskStatusSelect
                                    taskOccurrenceId={task.occurrence!.id}
                                    currentStatus={task.occurrence!.status}
                                />
                            ) : (
                                <TaskStatus status={task.occurrence!.status} />
                            )
                        }
                    </Can>
                ) : (
                    <span
                        className="rounded-pill px-3 py-1 text-xs font-medium"
                        style={{ backgroundColor: config.bg, color: config.text }}
                    >
                        {t(config.labelKey)}
                    </span>
                )}
            </div>

            <section className="flex flex-col gap-2">
                <h3 className="text-label font-medium uppercase tracking-wider text-text-secondary">
                    {t("common.description")}
                </h3>
                <p className="text-body">
                    {task.description ? task.description : t("common.noDescriptionProvided")}
                </p>
            </section>

            <section className="flex flex-col gap-2">
                <h3 className="text-label font-medium uppercase tracking-wider text-text-secondary">
                    {t("common.details")}
                </h3>
                <div className="flex flex-wrap items-center gap-4 text-body">
                    {task.ownerUserId ? (
                        <span className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faUser} className="size-4" />
                            <UserDisplay userId={task.ownerUserId} />
                        </span>
                    ) : null}
                    <span className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendarAlt} className="size-4" />
                        {task.dueDate.toLocaleDateString(i18n.language, {
                            weekday: "short",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                        })}
                    </span>
                </div>
            </section>

            {task.links && task.links.length > 0 && (
                <section className="flex flex-col gap-2">
                    <h3 className="text-label font-medium uppercase tracking-wider text-text-secondary">
                        {t("common.relatedLinks")}
                    </h3>
                    <ul className="flex flex-col gap-2">
                        {task.links.map((link) => (
                            <li key={link.id} className="flex items-center gap-2 min-w-0">
                                <FontAwesomeIcon icon={faLink} className="size-4 shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-body hover:underline truncate block"
                                    >
                                        {link.title || link.url}
                                    </a>
                                    <span className="text-micro text-text-secondary font-mono truncate block">
                                        {link.url}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <DialogFooter>
                <div className="flex justify-end items-center w-full gap-2">
                    <Button
                        type="button"
                        className="rounded-pill bg-compendia-foreground text-white hover:bg-compendia-foreground/90 px-5 py-2 text-sm font-semibold"
                        onClick={onClose}
                    >
                        {t("common.close")}
                    </Button>
                </div>
            </DialogFooter>
        </div>
    );
}
